"use client";

import { hooks, apijs, providers } from "@lib/client";
import logjs from "@metrojs/logjs";
import Consts from "@common/config/consts";
import AccountInfoDialog from "./AccountInfoDialog";

const { useWebAppContext } = providers;
const api = new apijs("api/AccountInfoDialog");
const log = new logjs("dialogs/accountinfo/useAccountInfoDialog");

/**
 * アカウント情報ダイアログ用フック
 * - 画面フォームの状態管理
 * - サーバAPI呼び出し（ユーザー情報更新・パスワード更新・住所検索）
 * @param {Object} initState 初期状態
 * @param {Object} state 外部状態
 * @param {Object} actions 画面共通アクション（showAlert/showConfirm/showLoading/hideLoading 等）
 * @returns {[form, formProps]} フォームデータと操作関数
 */
export function useAccountInfoDialog(initState = {}, state, actions) {
  const { open, onClose, isOpen, renderDialog } = hooks.useCustomDialog(AccountInfoDialog);

  const [form, formProps] = hooks.useFormEx(initState, state, actions);
  const { params, searchParams } = useWebAppContext();

  // 初期データ取得処理（init）
  const init = async () => {
    log.debug("init START");
    actions.showLoading?.();
    try {
      const res = await api.post({ mode: "init" });
      log.debug("init END", res);

      if (res?.result === true && res.data) {
        const { corp, user, area, group } = res.data;
        formProps.setForm({
          ...form,
          ...corp,
          ...user,
          ...group,
          id: user?.id,
          registid:corp?.registid,
          groupName: group?.GroupName || "",
          corpName: corp?.CorpName || "",
          zipCode: corp?.ZipCode || "",
          todofuken: corp?.Todofuken || "",
          jyusho1: corp?.Jyusho1 || "",
          jyusho2: corp?.Jyusho2 || "",
          jyusho3: corp?.Jyusho3 || "",
          tel: corp?.Tel || "",
          fax: corp?.Fax || "",
          UserName: user?.UserName ?? "",
          Email: user?.Email ?? "",
          oldPassword: "",
          newPassword: "",
          newPassword2: "",
          areaList: area || [],
        });
      } else {
        throw new Error(res?.error || "アカウント情報の取得に失敗しました");
      }
    } catch (error) {
      log.error("init error", error);
    } finally {
      actions.hideLoading?.();
    }
  };

  /**
   * 郵便番号から住所を検索し、フォームへ反映
   * @param {string} zipCode 7桁郵便番号
   */
  async function searchAddress(zipCode) {
    log.debug("searchAddress START", { zipCode });
    if (!zipCode || zipCode.length !== 7) return;
    actions.showLoading?.();
    try {
      const result = await api.get({ mode: "searchAddress", zipCode });
      log.debug("searchAddress END", result);
      if (result?.result === true && result.address) {
        formProps.setForm({ ...form, todofuken: result.address.Address1 || "", zipCode: zipCode, jyusho1: result.address.Address2 || "", jyusho2: result.address.Address3 || "" });
      }
      return result;
    } catch (error) {
      log.error("searchAddress error", error);
      return { result: false, error: error.message };
    } finally {
      actions.hideLoading?.();
    }
  }

  /**
   * ユーザー情報の更新（氏名・メールなど） 旧ダイレクトオーダーの処理
   */
  // async function changeInfo() {
  //   log.debug("changeInfo START", form);
  //   actions.showLoading?.();
  //   try {
  //     const allPasswordEmpty =
  //       (!form.oldPassword || form.oldPassword.trim() === "") &&
  //       (!form.newPassword || form.newPassword.trim() === "") &&
  //       (!form.newPassword2 || form.newPassword2.trim() === "");

  //     let payload = {};

  //     if (allPasswordEmpty) {
  //       // パスワード欄がすべて空 → 通常のユーザー情報変更
  //       payload = {
  //         mode: "changeUser",
  //         Uid: form.id,
  //         UserName: form.UserName,
  //         Email: form.Email,
  //         UserID: form.UserID,
  //         Registid: form.registid,
  //       };
  //     } else {
  //       // パスワード欄のどれかに入力あり → パスワード変更処理
  //       payload = {
  //         mode: "changeInfo",
  //         Uid: form.id,
  //         UserName: form.UserName,
  //         Email: form.Email,
  //         oldPassword: form.Pswd,
  //         oldPassword2: form.oldPassword,
  //         newPassword: form.newPassword,
  //         newPassword2: form.newPassword2,
  //         UserID: form.UserID,
  //         Registid: form.registid,
  //       };
  //     }
  //     const result = await api.post(payload);
  //     log.debug("changeInfo END", result);
  //     return result;
  //   } catch (error) {
  //     log.error("changeInfo error", error);
  //     return { result: false, error: error.message };
  //   } finally {
  //     actions.hideLoading?.();
  //   }
  // }

  /**
   * ユーザー情報の更新（氏名・メールなど）
   */
  async function changeInfo() {
    log.debug("changeInfo START", form);
    actions.showLoading?.();
    try {
      const payload = {
        mode: "changeUser",
        Uid: form.id,
        UserName: form.UserName,
        Email: form.Email,
        UserID: form.UserID,
        Registid: form.registid,
      };
      const result = await api.post(payload);
      log.debug("changeInfo END", result);
      return result;
    } catch (error) {
      log.error("changeInfo error", error);
      return { result: false, error: error.message };
    } finally {
      actions.hideLoading?.();
    }
  }

  /**
   * パスワードの更新
   */
  async function changePassword() {
    log.debug("changePassword START");
    
    try {
      const payload = {
        mode: "changePassword",
        Uid: form.id,
        oldPassword: form.Pswd,
        oldPassword2: form.oldPassword,
        newPassword: form.newPassword,
        newPassword2: form.newPassword2,
        UserID: form.UserID,
        Registid: form.registid,
      };
      const result = await api.post(payload);
      log.debug("changePassword END", result);
      return result;
    } catch (error) {
      log.error("changePassword error", error);
      return { result: false, error: error.message };
    } finally {
      actions.hideLoading?.();
    }
  }

  return [
    {
      ...form,
      params,
      searchParams,
      isOpen,
    },
    {
      ...formProps,
      init,
      searchAddress,
      changeInfo,
      changePassword,
      renderDialog,
      open,
      onClose,
    },
  ];
}
